import dayjs from '~/timeInit';

import type { ExistingSurchargeDateBlock, ResolvedSurchargeBlock, SalesforceSurchargePeriod } from '~/types/surcharges';

import { daysOfWeek } from '~/utils/daysOfWeek';
import { resolveSurchargeOverlaps } from '~/utils/resolveSurchargeOverlaps';

import { isAccommodationPackage } from '../../../../typeNarrowingUtils';
import type { SurchargeRoomAssignment } from '../../types';

interface RoomRateSelection {
  roomType: App.RoomType;
  roomRate: App.RoomRate;
}

export function getApplicableNightCount(surcharge: SalesforceSurchargePeriod) {
  const startDate = dayjs.utc(surcharge.start_date).startOf('day');
  const endDate = dayjs.utc(surcharge.end_date).startOf('day');

  if (
    !startDate.isValid() ||
    !endDate.isValid() ||
    !startDate.isBefore(endDate, 'day') ||
    !daysOfWeek.some((day) => surcharge[day.key])
  ) {
    return 0;
  }

  let applicableNights = 0;
  let cursor = startDate;
  while (cursor.isBefore(endDate, 'day')) {
    const day = daysOfWeek.find((dayOfWeek) => dayOfWeek.fullName === cursor.format('dddd'));
    if (day && surcharge[day.key]) {
      applicableNights += 1;
    }
    cursor = cursor.add(1, 'day');
  }

  return applicableNights;
}

export function getOfferRoomRateSelections(
  packages: Array<App.Package> = [],
  roomTypes: Array<App.RoomType> = [],
): Array<RoomRateSelection> {
  const offerRoomRateIds = new Set<string>();

  packages.forEach((offerPackage) => {
    if (!isAccommodationPackage(offerPackage)) {
      return;
    }

    if (offerPackage.fk_room_rate_id) {
      offerRoomRateIds.add(String(offerPackage.fk_room_rate_id));
    }

    offerPackage.package_options?.forEach((packageOption) => {
      if (packageOption.fk_room_rate_id) {
        offerRoomRateIds.add(String(packageOption.fk_room_rate_id));
      }
    });
  });

  return roomTypes.flatMap((roomType) =>
    (roomType.room_rates ?? [])
      .filter((roomRate) => offerRoomRateIds.has(String(roomRate.id)))
      .map((roomRate) => ({ roomType, roomRate })),
  );
}

export function getOfferScopedRoomTypes(roomRateSelections: Array<RoomRateSelection>): Array<App.RoomType> {
  const roomTypesById = new Map<string, App.RoomType>();

  roomRateSelections.forEach(({ roomType, roomRate }) => {
    const existingRoomType = roomTypesById.get(roomType.id);

    if (existingRoomType) {
      existingRoomType.room_rates = [...existingRoomType.room_rates, roomRate];
      return;
    }

    roomTypesById.set(roomType.id, {
      ...roomType,
      room_rates: [roomRate],
    });
  });

  return Array.from(roomTypesById.values());
}

export function buildAppliedRoomsBySurchargeId({
  existingSurchargesByRoomRate,
  propertyId,
  roomRateSelections,
  surcharges,
  vendorId,
}: {
  existingSurchargesByRoomRate: Record<string, Array<ExistingSurchargeDateBlock>>;
  propertyId?: string;
  roomRateSelections: Array<RoomRateSelection>;
  surcharges: Array<SalesforceSurchargePeriod>;
  vendorId?: string;
}): Record<string, Array<SurchargeRoomAssignment>> {
  const assignmentBySurchargeId: Record<string, Array<SurchargeRoomAssignment>> = {};
  const resolvedBlocks = resolveSurchargeOverlaps(surcharges);

  surcharges.forEach((surcharge) => {
    assignmentBySurchargeId[surcharge.id] = [];
  });

  roomRateSelections.forEach(({ roomType, roomRate }) => {
    const existingBlocks = existingSurchargesByRoomRate[roomRate.id] ?? [];

    resolvedBlocks.forEach((resolvedBlock) => {
      if (!existingBlocks.some((block) => isMatchingResolvedSurchargeBlock(resolvedBlock, block))) {
        return;
      }

      resolvedBlock.sourceIds.forEach((surchargeId) => {
        assignmentBySurchargeId[surchargeId]?.push({
          href:
            vendorId && propertyId
              ? `/vendors/${vendorId}/properties/${propertyId}/room-types/${roomType.id}/room-rates/${roomRate.id}`
              : undefined,
          roomRateId: roomRate.id,
          roomTypeId: roomType.id,
          roomName: roomType.name,
          ratePlanName: roomRate.rate_plan?.name ?? 'Unknown rate plan',
          ratePlanCode: roomRate.rate_plan?.rate_plan_code,
        });
      });
    });
  });

  return assignmentBySurchargeId;
}

function isMatchingResolvedSurchargeBlock(resolvedBlock: ResolvedSurchargeBlock, block: ExistingSurchargeDateBlock) {
  return (
    (block.surcharge_type ?? 'vendor') === 'vendor' &&
    toDateKey(block.start_date) === toDateKey(resolvedBlock.start_date) &&
    toDateKey(block.end_date) === toDateKey(resolvedBlock.end_date) &&
    block.cost_currency === resolvedBlock.currency_code &&
    amountsEqual(block.cost_amount, resolvedBlock.cost_price) &&
    amountsEqual(block.prices?.[resolvedBlock.currency_code], resolvedBlock.sell_price) &&
    daysOfWeek.every((day) => Boolean(block[day.key]) === Boolean(resolvedBlock[day.key]))
  );
}

function toDateKey(value: string) {
  return dayjs.utc(value).format('YYYY-MM-DD');
}

function amountsEqual(left?: number, right?: number) {
  return typeof left === 'number' && typeof right === 'number' && Math.abs(left - right) < 0.01;
}
